// Miky Motorsport - nacitani a priprava 3D modelu aut (glTF, CC0 Quaternius) (M6+)
// Modely maji konzistentni strukturu: telo mesh + 3 kola (BackWheels, FrontLeft, FrontRight),
// materialy podle jmena: [0]=lak, Windows, Headlights, TailLights, Grey, Black.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

// klic -> soubor. q1/q6 = sporty (hrac/hero), zbytek = AI/varianty
export const CAR_MODEL_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'];
const TARGET_LEN = 4.35; // metry (delka auta) - sjednoceni meritka modelu
const MODEL_DROP = 0.55; // posun dolu, aby kola sedela na zemi (origin = stred chassis)

// materialy, ktere NEjsou lak (nechat barvu z modelu)
const FIXED_MATS = new Set(['Windows', 'Grey', 'Black', 'Headlights', 'TailLights']);

export async function loadCarModels() {
  const loader = new GLTFLoader();
  const out = {};
  await Promise.all(CAR_MODEL_KEYS.map((k) => new Promise((resolve) => {
    loader.load(`./models/${k}.glb`, (gltf) => {
      try { out[k] = prepModel(gltf.scene); } catch (e) { console.warn('prep model fail', k, e); }
      resolve();
    }, undefined, (err) => { console.warn('model load fail', k, err); resolve(); });
  })));
  return out;
}

// Normalizace: meritko na TARGET_LEN, delka podel +z, spodek na y=0, vystredit.
function prepModel(scene) {
  scene.updateMatrixWorld(true);
  let box = new THREE.Box3().setFromObject(scene);
  const size = box.getSize(new THREE.Vector3());
  // delka = delsi horizontalni rozmer; kdyz je podel x, otoc model o 90 st na z
  if (size.x > size.z) {
    scene.rotation.y = Math.PI / 2;
    scene.updateMatrixWorld(true);
    box = new THREE.Box3().setFromObject(scene);
  }
  const size2 = box.getSize(new THREE.Vector3());
  const s = TARGET_LEN / Math.max(size2.x, size2.z);
  const wrap = new THREE.Group();
  scene.scale.setScalar(s);
  scene.updateMatrixWorld(true);
  box = new THREE.Box3().setFromObject(scene);
  const c = box.getCenter(new THREE.Vector3());
  scene.position.set(-c.x, -box.min.y - MODEL_DROP, -c.z);
  wrap.add(scene);
  wrap.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });

  // Re-pivot kol: uzel kola je ve stredu auta, ale geometrie kola je posunuta na sve misto.
  // Rotace uzlu by tak kolo roztocila kolem stredu auta (kola "vyleti" pri jizde).
  // Posuneme geometrii do stredu uzlu a uzel na pozici kola -> rotace spravne tocti kolo na miste.
  wrap.traverse((o) => {
    if (o.isMesh || !/Wheel/i.test(o.name || '')) return;
    const b = new THREE.Box3();
    o.traverse((cc) => { if (cc.isMesh && cc.geometry) { cc.geometry.computeBoundingBox(); b.union(cc.geometry.boundingBox); } });
    if (b.isEmpty()) return;
    const ctr = b.getCenter(new THREE.Vector3());
    o.traverse((cc) => { if (cc.isMesh && cc.geometry) cc.geometry.translate(-ctr.x, -ctr.y, -ctr.z); });
    o.position.add(ctr);
    o.rotation.order = 'YXZ'; // steer (Y) pak roll (X)
  });
  return wrap;
}

// Vytvori instanci auta z prepped modelu: klon + vlastni materialy (lak/sklo/brzdy), kola.
export function instantiateCarModel(prepped, spec) {
  const group = prepped.clone(true);
  const paint = new THREE.Color(spec.color);
  let brakeMat = null;
  const wheels = [];

  group.traverse((o) => {
    if (o.isMesh) {
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      const newMats = mats.map((m) => {
        const name = m.name || '';
        if (name === 'Windows') {
          // tmave reflexivni polopruhledne sklo (odrazi HDRI, skryva prazdny interier)
          const nm = m.clone();
          nm.color = new THREE.Color(0x6f8ba0);
          nm.metalness = 0.0; nm.roughness = 0.05;
          nm.envMapIntensity = 2.4; nm.transparent = true; nm.opacity = 0.62;
          return nm;
        } else if (name === 'TailLights') {
          const nm = m.clone();
          nm.emissive = new THREE.Color(0xff1208); nm.emissiveIntensity = 0.85;
          brakeMat = nm; return nm;
        } else if (name === 'Headlights') {
          const nm = m.clone();
          nm.emissive = new THREE.Color(0xfff0cc); nm.emissiveIntensity = 1.2;
          return nm;
        } else if (!FIXED_MATS.has(name)) {
          // lak -> lesklý car-paint s clearcoatem (odrazí HDRI prostředí)
          const isDark = /dark/i.test(name);
          return new THREE.MeshPhysicalMaterial({
            color: isDark ? paint.clone().multiplyScalar(0.7) : paint.clone(),
            metalness: 0.0, roughness: 0.42,
            clearcoat: 0.6, clearcoatRoughness: 0.2, envMapIntensity: 0.85,
          });
        }
        return m.clone();
      });
      o.material = Array.isArray(o.material) ? newMats : newMats[0];
    }
    const n = o.name || '';
    // jen transform-uzly kol (Group), ne jejich child meshe (tire+rim) - jinak dvojita rotace
    if (/Wheel/i.test(n) && !o.isMesh) {
      wheels.push({ node: o, front: /Front/i.test(n), base: o.rotation.clone() });
    }
  });

  return { group, wheels, brakeMat, isModel: true };
}
